/**
 * Toast Labor Management
 * View of employee and job data from Toast POS
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { post, get, del, put } from 'aws-amplify/api';
import useToastAPI from '../hooks/useToastAPI';
import { useAuth } from '../contexts/AuthContext';
import ToastEmployeesView from '../components/toast-labor/ToastEmployeesView';
import ToastJobsView from '../components/toast-labor/ToastJobsView';
import ToastSchedulesView from '../components/toast-labor/ToastSchedulesView';
import ToastLaborAuditView from '../components/toast-labor/ToastLaborAuditView';
import './ToastLabor.css';

// React wrapper for vanilla JS ToastEmployeesView
const EmployeesViewWrapper = ({ employees, jobs, shifts, loading }) => {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const { getAuthToken } = useAuth();

  useEffect(() => {
    if (containerRef.current && !viewRef.current) {
      viewRef.current = ToastEmployeesView.init(containerRef.current, {
        employees,
        jobs,
        shifts,
        loading,
        getAuthToken,
        apiHelpers: { get, post, put, del }
      });
    }
    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.update({ employees, jobs, shifts, loading });
    }
  }, [employees, jobs, shifts, loading]);

  return <div ref={containerRef} className="employees-view-container" />;
};

const ToastLabor = () => {
  const toast = useToastAPI();
  const [activeView, setActiveView] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  // `loading` tracks the critical Team/Positions data (employees + jobs).
  // Shifts and time entries are fetched independently — Toast rate-limits
  // both endpoints and a slow/queued 429 shouldn't block the table from
  // rendering.
  const [loading, setLoading] = useState(true);
  const [shiftsLoading, setShiftsLoading] = useState(true);
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  // Get date range for shifts (current week)
  const getShiftDateRange = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 13); // 2 weeks out
    endOfWeek.setHours(23, 59, 59, 999);

    return {
      startDate: startOfWeek.toISOString(),
      endDate: endOfWeek.toISOString()
    };
  };

  const views = [
    { id: 'employees', label: 'Team', count: employees.filter(e => !e.deleted && !e.disabled).length },
    { id: 'schedules', label: 'Schedules', count: shifts.length },
    { id: 'jobs', label: 'Positions', count: jobs.length },
  ];

  const fetchAllData = useCallback(async ({ fresh = false } = {}) => {
    setLoading(true);
    setShiftsLoading(true);
    setTimeEntriesLoading(true);
    setError(null);

    const { startDate, endDate } = getShiftDateRange();
    const opts = fresh ? { fresh: true } : undefined;

    // Fetch shifts in parallel but don't let it block the critical employees
    // + jobs render. Toast rate-limits get_shifts and a queued 429 can take
    // 10–30s — without this split, the Team tab appears to hang.
    toast.getShifts(startDate, endDate, null, opts)
      .then(shiftsData => setShifts(shiftsData || []))
      .catch(() => setShifts([]))
      .finally(() => setShiftsLoading(false));

    // Pull worked time entries for the same week so the labor-audit panel
    // can toggle Scheduled vs Actual without a second round-trip.
    toast.getTimeEntries(startDate, endDate, null, null, opts)
      .then(entries => setTimeEntries(entries || []))
      .catch(() => setTimeEntries([]))
      .finally(() => setTimeEntriesLoading(false));

    try {
      const [employeesData, jobsData] = await Promise.all([
        toast.getEmployees(null, opts).catch(() => []),
        toast.getJobs(null, opts).catch(() => []),
      ]);

      setEmployees(employeesData || []);
      setJobs(jobsData || []);
      setLastSync(new Date());
    } catch (err) {
      console.error('Error fetching Toast data:', err);
      setError(err.message || 'Failed to sync with Toast POS');
    }

    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Refresh button should bypass the Toast proxy's cache — a manual refresh
  // is exactly when stale data is unwanted.
  const handleRefresh = () => {
    fetchAllData({ fresh: true });
  };

  const formatLastSync = () => {
    if (!lastSync) return 'Never';
    const now = new Date();
    const diff = Math.floor((now - lastSync) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return lastSync.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getActiveEmployeeCount = () => {
    return employees.filter(e => !e.deleted && !e.disabled).length;
  };

  return (
    <div className="toast-labor">
      {/* Header */}
      <header className="toast-labor-header">
        <div className="header-content">
          <div className="header-title-section">
            <div className="toast-badge">
              <span className="toast-label">Toast POS</span>
              <span className={`sync-status ${loading ? 'syncing' : 'synced'}`}>
                {loading ? 'Syncing' : 'Live'}
              </span>
            </div>
            <h1>Labor Management</h1>
          </div>
          <div className="header-actions">
            <div className="sync-info">
              <span className="sync-time">Last sync: {formatLastSync()}</span>
              <button
                className="refresh-btn"
                onClick={handleRefresh}
                disabled={loading}
                title="Refresh data from Toast"
              >
                <span className={`refresh-icon ${loading ? 'spinning' : ''}`}>↻</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stat-card">
            <span className="stat-value">{getActiveEmployeeCount()}</span>
            <span className="stat-label">Active Staff</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{shifts.length}</span>
            <span className="stat-label">Scheduled Shifts</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{jobs.length}</span>
            <span className="stat-label">Positions</span>
          </div>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span className="error-message">{error}</span>
          <button className="error-dismiss" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Navigation */}
      <nav className="toast-labor-nav">
        <div className="nav-tabs">
          {views.map(view => (
            <button
              key={view.id}
              className={`nav-tab ${activeView === view.id ? 'active' : ''}`}
              onClick={() => setActiveView(view.id)}
            >
              <span className="nav-label">{view.label}</span>
              <span className="nav-count">{view.count}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Content */}
      <main className="toast-labor-content">
        {loading && employees.length === 0 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Connecting to Toast POS...</p>
          </div>
        ) : (
          <>
            {activeView === 'employees' && (
              <>
                <ToastLaborAuditView
                  shifts={shifts}
                  timeEntries={timeEntries}
                  employees={employees}
                  jobs={jobs}
                  shiftsLoading={shiftsLoading}
                  timeEntriesLoading={timeEntriesLoading}
                />
                <EmployeesViewWrapper
                  employees={employees}
                  jobs={jobs}
                  shifts={shifts}
                  loading={loading}
                />
              </>
            )}
            {activeView === 'schedules' && (
              <ToastSchedulesView
                shifts={shifts}
                employees={employees}
                jobs={jobs}
                loading={loading || shiftsLoading}
              />
            )}
            {activeView === 'jobs' && (
              <ToastJobsView
                jobs={jobs}
                employees={employees}
                loading={loading}
              />
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="toast-labor-footer">
        <div className="footer-content">
          <span className="powered-by">
            Powered by <strong>Toast POS</strong> Integration
          </span>
          <span className="data-note">
            Data synced from your restaurant's Toast point-of-sale system
          </span>
        </div>
      </footer>
    </div>
  );
};

export default ToastLabor;
